import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Radio from "antd/es/radio";
import { useIntl } from "react-intl";

import {
  EntitiesSupportingBulkEdit,
  EntityType,
  EntityTypesSupportingRoutes,
  Route,
} from "@mapmycustomers/shared/types/entity";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { Labeled, SelectField } from "@mapmycustomers/ui";

import CreateRouteModal from "@app/component/AddToRouteModal/CreateRouteModal";
import ButtonLink from "@app/component/ButtonLink";
import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";

import styles from "../../CommonModals.module.scss";
import BaseModal from "../BaseModal";
import Divider from "../Divider";
import EntityTypeSelector from "../EntityTypeSelector";
import IncludeDealsSelector from "../IncludeDealsSelector";

import messages from "./utils/messages";
import useSubmitHandler from "./utils/useSubmitHandler";
// FIXME: do not use anything from AddToRouteModal in SelectBar! (except related to CreateRoute, but even there it's better to separate CreateRouteModal from AddToRouteModal store)
import { isCreateRouteModalVisible, isCreating } from "@app/component/AddToRouteModal/store";
import { showCreateRouteModal } from "@app/component/AddToRouteModal/store/actions";
import RadioGroup from "@app/component/input/RadioGroup";
import {
  getExistingRoutesIds,
  getRoutes,
  getTotalRoutes,
  isRouteModalInitializing,
  isUpdating,
} from "@app/component/view/SelectBar/store";
import {
  bulkAddEntitiesToRoutes,
  bulkRemoveEntitiesFromRoutes,
  filterRoutes,
  initializeRouteModal,
  updateRoutes,
} from "@app/component/view/SelectBar/store/actions";
import BulkRouteOperation, { getBulkRouteOperationDisplayName } from "@app/enum/BulkRouteOperation";
import { isGeocodeMmcLimitReached, isGeocodeOrgLimitReached } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import showGeocodeLimitAlertModal from "@app/util/geocodeLimitAlert/showGeocodeLimitAlertModal";
import defaultSelectOptionMapper from "@app/util/mappers/defaultSelectOptionMapper";

interface Props {
  entities: Array<EntitiesSupportingBulkEdit>;
  existingRoutesIds: Set<Route["id"]>;
  isMmcGeocodeLimitReached: boolean;
  isOrgGeocodeLimitReached: boolean;
  onHide: () => void;
  onReloadData?: () => void;
}

type ComponentProps = Props & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

type RouteId = Route["id"];
type RouteIdArray = Array<RouteId>;

const RoutesModal: React.FC<ComponentProps> = ({
  bulkAddEntitiesToRoutes,
  bulkRemoveEntitiesFromRoutes,
  createRouteModalVisible,
  creating,
  entities,
  existingRoutesIds,
  initialize,
  initializing,
  isMmcGeocodeLimitReached,
  isOrgGeocodeLimitReached,
  onHide,
  onReloadData,
  onSearch,
  onUpdateRoutes,
  routes,
  showCreateRouteModal,
  totalRoutes,
  updating,
}) => {
  const intl = useIntl();
  const analyticsIssuer = useAnalytics();
  useEffect(() => {
    analyticsIssuer.using(["Add/Remove from Routes"]);
  }, [analyticsIssuer]);
  const [routeIdsToAdd, setRouteIdsToAdd] = useState<RouteIdArray>([]);
  const [routeIdsToRemove, setRouteIdsToRemove] = useState<RouteIdArray>([]);
  const [operation, setOperation] = useState<BulkRouteOperation>(BulkRouteOperation.ADD);
  const [includeDeals, setIncludeDeals] = useState<boolean>(true);

  const [companyLassoed, personLassoed, dealLassoed] = useMemo(() => {
    const types = new Set(entities.map(({ entity }) => entity));
    return [
      types.has(EntityType.COMPANY),
      types.has(EntityType.PERSON),
      types.has(EntityType.DEAL),
    ];
  }, [entities]);

  const [entityType, setEntityType] = useState<EntityTypesSupportingRoutes>(
    companyLassoed || !personLassoed ? EntityType.COMPANY : EntityType.PERSON
  );

  useEffect(() => {
    initialize({ entities, entityType, includeDeals });
    setRouteIdsToAdd([]);
    setRouteIdsToRemove([]);
  }, [entities, entityType, includeDeals, initialize, setRouteIdsToAdd, setRouteIdsToRemove]);

  const routesOptions = useMemo(() => {
    let result = routes;
    if (operation === BulkRouteOperation.REMOVE) {
      result = result.filter(({ id }) => existingRoutesIds.has(id));
    }
    return result.map(defaultSelectOptionMapper);
  }, [existingRoutesIds, routes, operation]);

  const handleCreate = useCallback(() => {
    if (isMmcGeocodeLimitReached || isOrgGeocodeLimitReached) {
      showGeocodeLimitAlertModal(intl, isMmcGeocodeLimitReached);
    } else {
      showCreateRouteModal();
    }
  }, [intl, isMmcGeocodeLimitReached, isOrgGeocodeLimitReached, showCreateRouteModal]);

  const handleChange = useCallback(
    (values) => {
      if (operation === BulkRouteOperation.ADD) {
        setRouteIdsToAdd(values);
      } else {
        setRouteIdsToRemove(values);
      }
    },
    [operation, setRouteIdsToAdd, setRouteIdsToRemove]
  );

  const hasOptions = totalRoutes > 0;
  const isAdding = operation === BulkRouteOperation.ADD;
  const routeIds = isAdding ? routeIdsToAdd : routeIdsToRemove;
  const hasValue = !!routeIds && routeIds.length > 0;
  const isLoading = initializing || creating || updating;
  const isApplyDisabled = !hasOptions || !hasValue || isLoading;

  const handleRouteCreated = useCallback(
    (route) => {
      initialize({
        callback: () => {
          onUpdateRoutes(route);
          setRouteIdsToAdd((selectedIds) => [...selectedIds, route.id]);
        },
        entities,
        entityType,
        includeDeals,
      });
    },
    [entities, entityType, includeDeals, initialize, onUpdateRoutes]
  );

  const handleSearch = useDebouncedCallback(
    [
      (value: string) => {
        onSearch({ entityType, query: value });
      },
      500,
    ],
    [entityType, onSearch]
  );

  const handleBlur = useCallback(() => {
    onSearch({ entityType });
  }, [entityType, onSearch]);

  const handleApply = useSubmitHandler(
    bulkAddEntitiesToRoutes,
    bulkRemoveEntitiesFromRoutes,
    operation,
    entityType,
    entities,
    routeIds,
    includeDeals,
    useCallback(() => {
      analyticsIssuer.using(["Add/Remove from Routes", "Apply"]);
      onHide();
      onReloadData?.();
    }, [analyticsIssuer, onHide, onReloadData])
  );

  return (
    <BaseModal
      applyDisabled={isApplyDisabled}
      applyLoading={updating}
      onApply={handleApply}
      onHide={onHide}
      title={intl.formatMessage(messages.title)}
    >
      {dealLassoed && (
        <div className={styles.information}>{intl.formatMessage(messages.information)}</div>
      )}
      {companyLassoed === personLassoed && (
        <EntityTypeSelector
          entityTypes={[EntityType.COMPANY, EntityType.PERSON]}
          onChange={(type) => setEntityType(type as EntityTypesSupportingRoutes)}
          value={entityType}
        />
      )}
      {dealLassoed && (companyLassoed || personLassoed) && (
        <IncludeDealsSelector
          onChange={setIncludeDeals}
          relatedEntityType={entityType}
          value={includeDeals}
        />
      )}
      {Number(dealLassoed) + Number(companyLassoed) + Number(personLassoed) > 1 && <Divider />}

      <Labeled label={intl.formatMessage(messages.selectOne)}>
        <RadioGroup className={styles.operation} onChange={setOperation} value={operation}>
          <Radio.Button value={BulkRouteOperation.ADD}>
            {getBulkRouteOperationDisplayName(intl, BulkRouteOperation.ADD)}
          </Radio.Button>

          <Radio.Button disabled={!hasOptions} value={BulkRouteOperation.REMOVE}>
            {getBulkRouteOperationDisplayName(intl, BulkRouteOperation.REMOVE)}
          </Radio.Button>
        </RadioGroup>
      </Labeled>

      <Labeled label={intl.formatMessage(messages.selectGroup)}>
        <SelectField<RouteIdArray>
          className={styles.select}
          disabled={!hasOptions || isLoading}
          filterOption={defaultFilterOption}
          loading={isLoading}
          mode="multiple"
          onBlur={handleBlur}
          onChange={handleChange}
          onSearch={handleSearch}
          options={routesOptions}
          placeholder={intl.formatMessage(messages.groupsPlaceholder)}
          showArrow
          showSearch
          value={routeIds}
        />

        {isAdding ? (
          <ButtonLink className={styles.addButton} onClick={handleCreate}>
            {intl.formatMessage(messages.create)}
          </ButtonLink>
        ) : null}
      </Labeled>

      {createRouteModalVisible ? (
        <CreateRouteModal
          createOnly
          entityType={entityType}
          onCreated={handleRouteCreated}
          routes={routes}
        />
      ) : null}
    </BaseModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  createRouteModalVisible: isCreateRouteModalVisible(state),
  creating: isCreating(state),
  existingRoutesIds: new Set(getExistingRoutesIds(state)),
  initializing: isRouteModalInitializing(state),
  isMmcGeocodeLimitReached: isGeocodeMmcLimitReached(state),
  isOrgGeocodeLimitReached: isGeocodeOrgLimitReached(state),
  routes: getRoutes(state),
  totalRoutes: getTotalRoutes(state),
  updating: isUpdating(state),
});

const mapDispatchToProps = {
  bulkAddEntitiesToRoutes: bulkAddEntitiesToRoutes.request,
  bulkRemoveEntitiesFromRoutes: bulkRemoveEntitiesFromRoutes.request,
  initialize: initializeRouteModal.request,
  onSearch: filterRoutes.request,
  onUpdateRoutes: updateRoutes,
  showCreateRouteModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(RoutesModal);
